import React from 'react';
import type { AppData } from '../data/apps';
import { LockIcon } from './Icons';

interface AppCardProps {
  app: AppData;
  isLoggedIn: boolean;
  onOpenAuth: () => void;
}

const STATUS_BADGE_CLASS: Record<string, string> = {
  'beta': 'fs-badge fs-badge--beta',
  'alpha': 'fs-badge fs-badge--alpha',
  'stable': 'fs-badge fs-badge--stable',
  'coming-soon': 'fs-badge fs-badge--soon',
};

const STATUS_LABEL: Record<string, string> = {
  'beta': 'BETA',
  'alpha': 'ALPHA',
  'stable': 'STABLE',
  'coming-soon': 'Coming Soon',
};

export const AppCard: React.FC<AppCardProps> = ({ app, isLoggedIn, onOpenAuth }) => {
  const isActive = app.status === 'beta' || app.status === 'alpha';
  const isComing = app.status === 'coming-soon';

  const cardClasses = [
    'fs-card',
    'fs-reveal',
    isActive && 'fs-card--active',
    isComing && 'fs-card--coming',
    `fs-card--${app.system}`,
  ].filter(Boolean).join(' ');

  const iconClass = isActive
    ? `fs-card__icon fs-card__icon--${app.system}`
    : 'fs-card__icon fs-card__icon--coming';

  const systemTagClass = `fs-system-tag fs-system-tag--${app.system}`;
  const systemLabel = app.system === 'decision' ? 'Decision System' : 'Production System';

  return (
    <div className={cardClasses}>
      <div className="fs-card__inner">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div className={iconClass}>
            <img src={app.iconSrc} alt="" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
            <span className={STATUS_BADGE_CLASS[app.status]}>
              {STATUS_LABEL[app.status]}{app.version ? ` ${app.version}` : ''}
            </span>
            {app.isFree && <span className="fs-badge fs-badge--free">Free</span>}
          </div>
        </div>

        {/* Info */}
        <p className={systemTagClass}>{systemLabel}</p>
        <h3 className="fs-card__title">
          {app.name}{app.name ? ' ' : ''}<strong>{app.nameBold}</strong>
        </h3>
        <p className="fs-card__subtitle">{app.subtitle}</p>
        <p className="fs-card__description">{app.description}</p>

        {/* CTAs */}
        <div style={{ display: 'flex', gap: 8 }}>
          {isActive ? (
            <>
              {app.launchUrl ? (
                <a
                  href={app.launchUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="fs-btn-primary"
                  style={{ flex: 1, textAlign: 'center', fontSize: 12, padding: '10px 16px', textDecoration: 'none' }}
                >
                  Launch
                </a>
              ) : (
                <button className="fs-btn-primary" style={{ flex: 1, textAlign: 'center', fontSize: 12, padding: '10px 16px' }}>
                  Launch
                </button>
              )}
              <button className="fs-btn-ghost" style={{ fontSize: 12, padding: '10px 16px' }}>
                Learn more
              </button>
            </>
          ) : (
            <button
              className="fs-btn-ghost"
              style={{ flex: 1, textAlign: 'center', fontSize: 12, padding: '10px 16px', opacity: 0.5, cursor: 'not-allowed' }}
              disabled
            >
              Notify me
            </button>
          )}
        </div>
      </div>

      {/* Locked overlay (visitor, active cards only) */}
      {isActive && (
        <div className="fs-locked-overlay" onClick={onOpenAuth} style={{ cursor: 'pointer' }}>
          <div style={{ color: 'var(--fs-cyan)', marginBottom: 12 }}>
            <LockIcon size={28} />
          </div>
          <p style={{
            fontFamily: 'var(--fs-font-headline)',
            fontWeight: 700,
            fontSize: 14,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: 4,
            color: 'var(--fs-text)',
          }}>Sign in to use</p>
          <p style={{ fontSize: 11, color: 'var(--fs-text-muted)' }}>Free · 5 exports / week</p>
        </div>
      )}

      {/* Coming soon overlay */}
      {isComing && (
        <div className="fs-coming-overlay">
          <span className="fs-badge fs-badge--soon" style={{ marginBottom: 8 }}>Coming Soon</span>
          <p style={{ fontSize: 11, color: 'var(--fs-text-muted)' }}>In development</p>
        </div>
      )}
    </div>
  );
};
